import { AfterViewInit, Component, DestroyRef, ElementRef, inject, OnDestroy, ViewChild } from '@angular/core';
import { DISPLAY_ORIGIN } from '../../../database-model/models/constant/constants.constant';
import { DisplayCoordinateDto } from 'src/app/core/model/coordinates/display-coordinate.dto';
import { JavaClass } from '../models/java-class.class';
import { UmlService } from '../../services/uml.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ZERO } from '../../../../core/constant/number.constant';
import { Rect } from '../../../../core/model/coordinates/rect.type';
import { Area } from '../../../../core/model/coordinates/area.class';
import { CHARACTER_WIDTH, LINE_HEIGHT, MARGIN, TEXT_LINE_SHIFT } from '../../constants/uml.constant';

@Component({
  selector: 'app-uml-canvas',
  standalone: true,
  imports: [],
  template: `<canvas #canvas class="diagram-background"></canvas>`,
  styleUrl: './uml-canvas.component.scss',
})
export class UmlCanvasComponent implements AfterViewInit, OnDestroy {
  @ViewChild('canvas', { static: true }) canvasRef!: ElementRef<HTMLCanvasElement>;
  private _ctx!: CanvasRenderingContext2D;
  private _canvas!: HTMLCanvasElement;
  private _umlService = inject(UmlService);
  private _destroyRef = inject(DestroyRef);

  javaClass!: JavaClass;

  private _font = '18px monospace';

  ngAfterViewInit(): void {
    this._canvas = this.canvasRef.nativeElement;
    this._ctx = this._canvas.getContext('2d')!;
    this._umlService
      .getJavaClass$()
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe(javaClass => {
        this.javaClass = javaClass;
        this.resizeCanvas();
        this.draw();
      });

    window.addEventListener('resize', () => this.resizeCanvas());
  }

  ngOnDestroy(): void {
    window.removeEventListener('resize', () => this.resizeCanvas());
  }

  resizeCanvas(): void {
    const canvas = this.canvasRef.nativeElement;
    canvas.width = this.findMaxWidthAndHeight().x;
    canvas.height = this.findMaxWidthAndHeight().y;
  }

  findMaxWidthAndHeight(): DisplayCoordinateDto {
    let maxX = DISPLAY_ORIGIN.x;
    let maxY = DISPLAY_ORIGIN.y;

    let maxCharacters = ZERO;
    const numberLines = this._getNumberOfLines();

    for (const field of this.javaClass.fields) {
      maxCharacters = Math.max(maxCharacters, field.length);
    }
    for (const method of this.javaClass.methods) {
      maxCharacters = Math.max(maxCharacters, method.length);
    }

    maxX = maxCharacters * CHARACTER_WIDTH + 2 * MARGIN;
    maxY = numberLines * LINE_HEIGHT + 2 * MARGIN;

    return { x: maxX, y: maxY };
  }

  draw(): void {
    if (this.javaClass.isEmpty()) return;
    this._ctx.fillStyle = 'black';
    this._ctx.font = this._font;
    const startingCoordinates = { x: MARGIN, y: MARGIN };
    const width = this._canvas.width - 2 * MARGIN;
    const startingRect = { width: width, height: 0 };

    const titleArea = new Area(startingCoordinates, startingRect);
    let actualY = this._drawTitle(titleArea);

    const fieldsArea = new Area({ x: startingCoordinates.x, y: actualY }, startingRect);
    actualY = this._drawRectAndLines(fieldsArea, this.javaClass.fields);

    const methodsArea = new Area({ x: startingCoordinates.x, y: actualY }, startingRect);
    this._drawRectAndLines(methodsArea, this.javaClass.methods);
  }

  private _getNumberOfLines(): number {
    return this.javaClass.fields.length + this.javaClass.methods.length + 1 + this.javaClass.classModifier.length;
  }

  private _drawTitle(area: Area): number {
    const lines: string[] = [...this.javaClass.classModifier, this.javaClass.className];
    return this._drawRectAndLines(area, lines, true);
  }

  private _drawRectAndLines(area: Area, lines: string[], isTitle: boolean = false): number {
    const rectHeight = lines.length * LINE_HEIGHT;
    const rect: Rect = { width: area.width, height: rectHeight };
    const rectangleArea = new Area(area.coordinates, rect);

    this._drawRect(rectangleArea);

    let yLine = area.y + TEXT_LINE_SHIFT;

    for (const line of lines) {
      let x = area.x;
      if (isTitle) {
        x = x + (area.width / 2 - (line.length * CHARACTER_WIDTH) / 2);
      }
      this._ctx.fillText(line, x, yLine);
      yLine += LINE_HEIGHT;
    }

    return area.y + rectHeight;
  }

  private _drawRect(area: Area): void {
    this._ctx.strokeRect(area.x, area.y, area.width, area.height);
  }
}
